/**
 * Helpers must work both Client and Server side
 */
export function rgbStringToHex(rgbString: string): string {
  const components = rgbString.split(' ');

  if (components.length !== 3) {
    throw new Error('> Formato RGB inválido. Use o formato "R G B".');
  }

  const [r, g, b] = components.map(Number);

  if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
    throw new Error('> Valor de cor inválido, cada cor deve estar entre 0 e 255.');
  }

  const toHex = (color: number) => color.toString(16).padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function isValidHttpURL(url: string): boolean {
  try {
    const parsedUrl = new URL(url);

    return parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:';
  } catch (error) {
    return false;
  }
}

export function slugify(text: string): string {
  return text
    .toString() // convert to string
    .normalize('NFD') // normalize to decompose accented characters
    .replace(/[\u0300-\u036f]/g, '') // remove diacritics (accents)
    .toLowerCase() // convert to lowercase
    .trim() // remove whitespace from both ends
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/[^\w\-]+/g, '') // remove remaining special characters
    .replace(/\-\-+/g, '-'); // remove duplicate hyphens
}

export function removeHtmlFromString(string: string | null): string {
  return (
    string
      ?.replace(/<(style|svg)((.|\n|\r)*?)<\/(style|svg)>/gi, '') // remove SVG, STYLE tags
      .replace(/<(\/p|\/li)+>/gi, ' ') // replace closing tags to white space
      .replace(/(<([^>]+)>)/gi, '') // remove HTML tags
      .replace(/(&nbsp;)/gi, ' ') // replace unicode to white space
      .replace(/[\r\n]+/gi, ' ') // replace line breaks to white space
      .replace(/(image\ssource:)\s?(\w+)/gi, '') // remove "image source: {word} "
      .replace(/[^\p{L}\p{N}\p{P}\p{Z}^$\n]/gu, ' ') // remove emojis
      .replace(/\s\s+/g, ' ') // replace multiple whitespaces
      .trim() || ''
  );
}

export function removeWhiteSpacesFromString(string: string): string {
  return string.replace(/\s+/g, ' ').trim();
}

export function countWords(string: string): number {
  const cleanString = removeWhiteSpacesFromString(string);

  if (cleanString === '') {
    return 0;
  }

  // Count words spliting by whitespaces
  const words = cleanString.split(' ');

  return words.length;
}

export function trimSlashes(input: string): string {
  return input.trim().replace(/^\/|\/$/g, '');
}

export function regexValidation(input: string, pattern: string): boolean {
  const regex = new RegExp(pattern, 'i');

  return regex.test(input);
}

export function getUrlPathname(url: string, keepUrlQuery: boolean = false): string {
  try {
    const parsedUrl = new URL(url, 'http://placeholder');

    return keepUrlQuery ? `${parsedUrl.pathname}${parsedUrl.search}` : parsedUrl.pathname;
  } catch (error) {
    console.error('> Invalid URL:', error);
    return url;
  }
}

export function getCookie(cookiesString: string, name: string): string {
  const cookies = cookiesString.split('; ').reduce((acc: Record<string, string>, cookie) => {
    const [key, value] = cookie.split('=');

    acc[key] = value;

    return acc;
  }, {});

  return cookies[name] || '';
}

export function loadCsrfCookie(): void {
  fetch('/generate_csrf_token')
    .then((response) => response.json())
    .catch((error) => {
      console.error('CSRF generation failed:', error);
    });
}

export function isHtmlContent(content: string): boolean {
  const trimmedContent = content.trim();
  const htmlTagPattern = /^<([a-z]+)(\s|>)/i;
  const match = trimmedContent.match(htmlTagPattern);

  if (match) {
    const tagName = match[1];
    const closingTagPattern = new RegExp(`</${tagName}>`, 'i');

    return closingTagPattern.test(trimmedContent);
  }

  return false;
}

export function removePrefixAndSuffix(input: string, prefix: string, suffix: string): string {
  let result = input;

  if (result.startsWith(prefix)) {
    result = result.slice(prefix.length);
  }

  if (result.endsWith(suffix)) {
    result = result.slice(0, -suffix.length);
  }

  return result;
}

export function getFirstName(fullName: string): string {
  const normalizedName = fullName
    .replace(/[\s]+/g, ' ') // Multiple spaces to single
    .replace(/\s([,.!?:;])/g, '$1') // Remove space between ponctuation
    .replace(/^[^\w\s]|[^\w\s]$/g, '') // Remove leading and trailing special characters
    .trim();

  const nameParts = normalizedName.split(' ');

  return nameParts[0];
}

function isLocalStorageEnabled(): boolean {
  try {
    const key = `__storage__test`;

    localStorage.setItem(key, '');
    localStorage.removeItem(key);

    return true;
  } catch (error) {
    console.warn('localStorage is not available:', error);

    return false;
  }
}

export function getLocalStorageValue(key: string): any | false {
  if (!isLocalStorageEnabled()) {
    return;
  }

  const value = localStorage.getItem(key);

  return value !== null ? JSON.parse(value) : false;
}

export function setLocalStorageValue(key: string, value: any): void {
  if (!isLocalStorageEnabled()) {
    return;
  }

  localStorage.setItem(key, JSON.stringify(value));
}

export function clearLocalStorage(): void {
  if (!isLocalStorageEnabled()) {
    return;
  }

  localStorage.clear();
}

export function setExpirationDate(days: number): void {
  const today = new Date();

  setLocalStorageValue('expiration', today.setDate(today.getDate() + days));
}

export function validateExpirationDate(): boolean {
  const today = new Date();
  const expirationDay = getLocalStorageValue('expiration');

  if (!expirationDay || today >= expirationDay) {
    return false;
  }

  return true;
}

export function getQuizEndpoint(env: Record<string, any>) {
  return env.USE_MOCK_SERVER === 'true'
    ? 'https://api-mock.genyotech-account.workers.dev'
    : 'https://responda.genyotech.com';
}

export function selectByWeight(options: Record<string, string | number | undefined>): string {
  if (!Object.keys(options).length) {
    return '';
  }

  // Convert all weights to numbers and filter out invalid ones
  const entries = Object.entries(options)
    .map(([key, weight]) => {
      const numWeight = weight === undefined ? 0 : typeof weight === 'string' ? parseFloat(weight) : weight;
      return [key, isNaN(numWeight) ? 0 : numWeight] as [string, number];
    })
    .filter(([_, weight]) => weight > 0);

  if (!entries.length) {
    return Object.keys(options)[0] || '';
  }

  const totalWeight = entries.reduce((sum, [_, weight]) => sum + weight, 0);
  const random = Math.random() * totalWeight;
  let weightSum = 0;

  for (const [option, weight] of entries) {
    weightSum += weight;
    if (random <= weightSum) {
      return option;
    }
  }

  return entries[0][0]; // Fallback to first option with valid weight
}
